/**
 * Reducer for maintaining our latest knowledge of any server-side models. (At least, ones that
 * we're keeping in the redux store.)
 *
 * For now, it can be used to save and access file to_summary_dicts loaded from the server, e.g. to
 * access tags. Note that this does not yet listen to all events that might update information about
 * files. In the future, though, file management may be moved to a sub-reducer.
 */
import produce from 'immer';

import {Collaborator} from 'auth/PermissionConstants';
import reducePolicies, {POLICIES_INITIAL_STATE, PoliciesState} from 'auth/reducePolicies';
import * as EventConstants from 'constants/EventConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import reduceInaccessibleModels, {
  INACCESSIBLE_MODELS_INITIAL_STATE,
  InaccessibleModelsState,
} from 'models/reduceInaccessibleModels';
import reduceLoadingState, {MODEL_LOADING_INITIAL_STATE, ModelLoadingState} from 'models/reduceLoadingState';
import reduceManagedModels, {
  MANAGED_MODELS_INITIAL_STATE,
  ManagedModelsState,
} from 'models/reduceManagedModels';
import {EntrySummary} from 'notebook/ELNConstants';
import reduceReviewCriteria, {
  REVIEW_CRITERIA_INITIAL_STATE,
  ReviewCriteriaState,
} from 'notebook/reviews/reduceReviewCriteria';
import reduceStringResolution, {
  STRING_RESOLUTION_INITIAL_STATE,
  StringResolutionState,
} from 'notebook/tables/string-resolution/reduceStringResolution';
import reduceTransferTableModels, {
  TRANSFER_TABLE_MODELS_INITIAL_STATE,
  TransferTableModelsState,
} from 'notebook/transfer-tables/reduceTransferTableModels';
import {BenchlingAction} from 'redux-stores/Action';
import reduceRequests, {REQUESTS_INITIAL_STATE, RequestsState} from 'requests/reduceRequests';
import reduceResults, {RESULTS_INITIAL_STATE, ResultsState} from 'results/reduceResults';
import {ValidationExtras} from 'schemas/SchemaValidationConstants';
import reduceStars, {STARS_INITIAL_STATE, StarsState} from 'stars/reduceStars';
import reduceAdminModels, {ADMIN_INITIAL_STATE, UserAdminState} from 'useradmin/reduceAdminModels';
import {expectNotImporting} from 'util/ImportTracker';
import reduceWarehouse, {WAREHOUSE_INITIAL_STATE, WarehouseState} from 'warehouse/reduceWarehouse';
import reduceSampleGroups, {
  SAMPLE_GROUPS_INITIAL_STATE,
  SampleGroupsState,
} from 'workflows/reduceSampleGroups';
import reduceWorkflows, {WORKFLOWS_INITIAL_STATE, WorkflowsState} from 'workflows/reduceWorkflows';
import {RegistrationRecordList} from 'workflows/WorkflowConstants';

import {ModelsActionType} from './ModelActions';

export interface ModelsState {
  managed: ManagedModelsState;
  useradmin: UserAdminState;
  eln: StringResolutionState;
  entrySummariesById: {[id: string]: EntrySummary};
  fileSummariesById: {[id: string]: BioentitySummary};
  validationExtrasByBioentityId: {[id: string]: ValidationExtras};
  // Ids of batches that we've confirmed have been deleted or aren't readable
  inaccessibleBatchIds: Array<string>;
  // Ids of entries that we've confirmed have been deleted or aren't readable
  inaccessibleEntryIds: Array<string>;
  // Ids of bioentities that we've confirmed have been deleted or aren't readable
  inaccessibleFileIds: Array<string>;
  inaccessibleState: InaccessibleModelsState;
  // Ids of storables that we've confirmed have been deleted or aren't readable
  inaccessibleStorableIds: Array<string>;
  loadingState: ModelLoadingState;
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  collaboratorsByOwnableId: {[id: string]: Array<Collaborator>};
  policies: PoliciesState;
  requests: RequestsState;
  results: ResultsState;
  reviews: ReviewCriteriaState;
  sampleGroups: SampleGroupsState;
  stars: StarsState;
  transferTables: TransferTableModelsState;
  warehouse: WarehouseState;
  workflows: WorkflowsState;
}

export const MODELS_INITIAL_STATE: ModelsState = {
  managed: MANAGED_MODELS_INITIAL_STATE,
  useradmin: ADMIN_INITIAL_STATE,
  eln: STRING_RESOLUTION_INITIAL_STATE,
  entrySummariesById: {},
  fileSummariesById: {},
  validationExtrasByBioentityId: {},
  inaccessibleBatchIds: [],
  inaccessibleEntryIds: [],
  inaccessibleFileIds: [],
  inaccessibleState: INACCESSIBLE_MODELS_INITIAL_STATE,
  // Ids of storables that we've confirmed have been deleted or aren't readable
  inaccessibleStorableIds: [],
  loadingState: MODEL_LOADING_INITIAL_STATE,
  registrationRecordListsById: {},
  collaboratorsByOwnableId: {},
  policies: POLICIES_INITIAL_STATE,
  requests: REQUESTS_INITIAL_STATE,
  results: RESULTS_INITIAL_STATE,
  reviews: REVIEW_CRITERIA_INITIAL_STATE,
  sampleGroups: SAMPLE_GROUPS_INITIAL_STATE,
  stars: STARS_INITIAL_STATE,
  transferTables: TRANSFER_TABLE_MODELS_INITIAL_STATE,
  warehouse: WAREHOUSE_INITIAL_STATE,
  workflows: WORKFLOWS_INITIAL_STATE,
};

export default function reduceModels(
  state: ModelsState = MODELS_INITIAL_STATE,
  action: BenchlingAction
): ModelsState {
  expectNotImporting();
  state = {
    ...state,
    managed: reduceManagedModels(state.managed, action),
    eln: reduceStringResolution(state.eln, action),
    inaccessibleState: reduceInaccessibleModels(state.inaccessibleState, action),
    loadingState: reduceLoadingState(state.loadingState, action),
    requests: reduceRequests(state.requests, action),
    results: reduceResults(state.results, action),
    reviews: reduceReviewCriteria(state.reviews, action),
    sampleGroups: reduceSampleGroups(state.sampleGroups, action),
    stars: reduceStars(state.stars, action),
    useradmin: reduceAdminModels(state.useradmin, action),
    transferTables: reduceTransferTableModels(state.transferTables, action),
    warehouse: reduceWarehouse(state.warehouse, action),
    workflows: reduceWorkflows(state.workflows, action),
    policies: reducePolicies(state.policies, action),
  };
  switch (action.type) {
    case ModelsActionType.COLLABORATORS_FETCH_COMPLETED: {
      const {collaboratorsByOwnableId} = action;
      return produce(state, (draftState) => {
        draftState.collaboratorsByOwnableId = {
          ...draftState.collaboratorsByOwnableId,
          ...collaboratorsByOwnableId,
        };
      });
    }
    case ModelsActionType.ENTRIES_FETCH_COMPLETED: {
      const {entrySummariesById, inaccessibleEntryIds} = action;
      return produce(state, (draftState) => {
        draftState.entrySummariesById = {
          ...draftState.entrySummariesById,
          ...entrySummariesById,
        };
        draftState.inaccessibleEntryIds.push(...inaccessibleEntryIds);
      });
    }
    case EventConstants.FILES_FETCH_COMPLETED: {
      const {fileSummariesById, inaccessibleFileIds} = action;
      return produce(state, (draftState) => {
        draftState.fileSummariesById = {
          ...draftState.fileSummariesById,
          ...fileSummariesById,
        };
        draftState.inaccessibleFileIds.push(...inaccessibleFileIds);
      });
    }
    case ModelsActionType.VALIDATION_EXTRAS_FETCH_COMPLETED: {
      const {validationExtrasByBioentityId, inaccessibleFileIds} = action;
      return produce(state, (draftState) => {
        draftState.validationExtrasByBioentityId = {
          ...draftState.validationExtrasByBioentityId,
          ...validationExtrasByBioentityId,
        };
        draftState.inaccessibleFileIds.push(...inaccessibleFileIds);
      });
    }
    case ModelsActionType.REGISTRATION_RECORD_LISTS_FETCH_COMPLETED: {
      const {registrationRecordListsById} = action;
      return produce(state, (draftState) => {
        draftState.registrationRecordListsById = {
          ...draftState.registrationRecordListsById,
          ...registrationRecordListsById,
        };
      });
    }
    case ModelsActionType.STORABLES_FETCH_COMPLETED: {
      const {inaccessibleStorableIds} = action;
      return produce(state, (draftState) => {
        draftState.inaccessibleStorableIds.push(...inaccessibleStorableIds);
      });
    }
    case ModelsActionType.BATCHES_FETCH_COMPLETED: {
      const {inaccessibleBatchIds} = action;
      return produce(state, (draftState) => {
        draftState.inaccessibleBatchIds.push(...inaccessibleBatchIds);
      });
    }
    default:
      return state;
  }
}
